<script setup lang="ts">
import { storeToRefs } from "pinia";
import LxcError from "~/core/models/LxcError.class";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import { getArrayDelta } from "~/core/utils/array";
import { formatIsoDateToUtc } from "~/core/utils/date";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { versionCompare } from "~/core/utils/versionCompare";
import type { CampaignFunnelStep1 } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep1.vue";
import CampaignOperationMgrFunnelStep1 from "~/modules/campaign/components/CampaignOperationMgrFunnelStep1.vue";
import type { CampaignFunnelStep2 } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep2.vue";
import CampaignOperationMgrFunnelStep2 from "~/modules/campaign/components/CampaignOperationMgrFunnelStep2.vue";
import type { CampaignFunnelStep3 } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3.vue";
import CampaignOperationMgrFunnelStep3 from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3.vue";
import type { CampaignInclusionPeriodI } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3InclusionPeriods.vue";
import { CampaignCreationMode } from "~/modules/campaign/models/CampaignCreationMode.enum";
import { CampaignOMStatus } from "~/modules/campaign/models/CampaignOMStatus.enum";
import { CampaignOMType } from "~/modules/campaign/models/CampaignOMType.enum";
import { CUSTOM_INPUT_PARAMETERS_NAMES } from "~/modules/campaign/models/CampaignOperationMgr.enum";
import type { CampaignOperationMgr } from "~/modules/campaign/models/CampaignOperationMgr.interface";
import type { CampaignOperationMgrAddRemoveOperation } from "~/modules/campaign/models/CampaignOperationMgrAddRemoveOperation.interface";
import type { CampaignOperationMgrCreate } from "~/modules/campaign/models/CampaignOperationMgrCreate.interface";
import type { CampaignOperationMgrUpdate } from "~/modules/campaign/models/CampaignOperationMgrUpdate.interface";
import type { CampaignOperationParameter } from "~/modules/campaign/models/CampaignOperationParameter.interface";
import CampaignOperationMgrService from "~/modules/campaign/services/campaignOperationMgr.service";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { getCustomInputParameters } from "~/utils/getCustomInputParameters";
import ILxcAlertCircle from "~icons/lxc/alert-circle";
import ILxcArrowLeft from "~icons/lxc/arrow-left";

const props = defineProps<{
  campaign?: CampaignOperationMgr;
  show?: boolean;
}>();

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const emit = defineEmits(["update:show", "saved"]);

const step = ref(1);

// Possibility to edit the campaign on:
//   - creation
//   - or modification if the campaign is scheduled only
const canEditCampaign = computed(
  () => !props.campaign || props.campaign.status === CampaignOMStatus.SCHEDULED,
);

const campaignOperationMgrFunnelStep1: Ref<
  typeof CampaignOperationMgrFunnelStep1 | undefined
> = ref();
const campaignOperationMgrFunnelStep2: Ref<
  typeof CampaignOperationMgrFunnelStep2 | undefined
> = ref();
const campaignOperationMgrFunnelStep3: Ref<
  typeof CampaignOperationMgrFunnelStep3 | undefined
> = ref();

const isSubmitting = ref(false);

const campaignFunnelStep1: Ref<CampaignFunnelStep1> = ref({
  name: undefined,
  operationModelType: undefined,
  firmwareUuid: undefined,
});

const campaignFunnelStep2: Ref<CampaignFunnelStep2> = ref({
  dtwins: [],
  fleets: [],
});

const campaignFunnelStep3: Ref<CampaignFunnelStep3> = ref({
  dateRange: {
    startAt: undefined,
    endAt: undefined,
  },
  inclusionPeriods: {
    periods: [],
  },
});

const isCurrentStepValid: Ref<boolean> = computed(() => {
  let isValid = false;
  switch (step.value) {
    case 1:
      isValid = campaignOperationMgrFunnelStep1?.value?.isValid;
      break;
    case 2:
      isValid = campaignOperationMgrFunnelStep2?.value?.isValid;
      break;
    case 3:
      isValid = campaignOperationMgrFunnelStep3?.value?.isValid;
      break;
  }
  return isValid;
});

const selectedFirmware: Ref<Firmware | undefined> = ref();
const showConfirmActionsInProgress: Ref<boolean> = ref(false);

/**
 * Reset the campaign funnel from campaign parameter
 */
function resetCampaignFunnel(campaign?: CampaignOperationMgr) {
  resetCampaignFunnelStep1(campaign);
  resetCampaignFunnelStep2(campaign);
  resetCampaignFunnelStep3(campaign);
}

/**
 * reset the campaign funnel step 1 from campaign parameter
 */
function resetCampaignFunnelStep1(campaign?: CampaignOperationMgr) {
  campaignFunnelStep1.value.name = campaign?.name;

  // get the first operation type of the campaign until composite campaign managed in frontend
  campaignFunnelStep1.value.operationModelType = campaign?.operationTypes[0];

  if (
    campaign?.operationParametersList &&
    campaign.operationParametersList.length > 0
  ) {
    campaignFunnelStep1.value.firmwareUuid = campaign.operationParametersList[0]
      .customInputParameters?.firmwareUid as string;
  } else {
    campaignFunnelStep1.value.firmwareUuid = undefined;
  }
}

/**
 * reset the campaign funnel step 2 from campaign parameter
 */
function resetCampaignFunnelStep2(campaign?: CampaignOperationMgr) {
  if (campaign) {
    // Nothing to do here, selected devices and fleets are set in the component campaignFunnelStep2
  } else {
    campaignFunnelStep2.value.dtwins = [];
    campaignFunnelStep2.value.fleets = [];
  }
}

/**
 * reset the campaign funnel step 3 from campaign parameter
 */
function resetCampaignFunnelStep3(campaign?: CampaignOperationMgr) {
  resetCampaignFunnelStep3DateRange(campaign);
  resetCampaignFunnelStep3InclusionPeriods(campaign);
}

/**
 * reset the campaign funnel step 3 date range from campaign parameter
 */
function resetCampaignFunnelStep3DateRange(campaign?: CampaignOperationMgr) {
  if (campaign?.launchPlannedAt && campaign?.expiredAt) {
    campaignFunnelStep3.value.dateRange.startAt = new Date(
      campaign.launchPlannedAt,
    );
    campaignFunnelStep3.value.dateRange.endAt = new Date(campaign.expiredAt);
  } else {
    campaignFunnelStep3.value.dateRange.startAt = undefined;
    campaignFunnelStep3.value.dateRange.endAt = undefined;
  }
}

/**
 * reset the campaign funnel step 3 inclusion periods from campaign parameter
 */
function resetCampaignFunnelStep3InclusionPeriods(
  campaign?: CampaignOperationMgr,
) {
  if (campaign && campaign.operationParametersList) {
    const operationParameterWithInclusionPeriods =
      campaign.operationParametersList.find((operationParameter) => {
        if (operationParameter.customInputParameters) {
          return operationParameter.customInputParameters[
            CUSTOM_INPUT_PARAMETERS_NAMES.INCLUSION_PERIODS
          ];
        }
      });
    if (operationParameterWithInclusionPeriods) {
      campaignFunnelStep3.value.inclusionPeriods.periods =
        operationParameterWithInclusionPeriods?.customInputParameters
          ?.inclusionPeriods as CampaignInclusionPeriodI[];
    }
  } else {
    campaignFunnelStep3.value.inclusionPeriods.periods = [];
  }
}

const operationModels: Ref<OperationModel[]> = ref([]);

/**
 * Go to the next step
 */
async function nextStep() {
  if (step.value === 1) {
    const isValid = await campaignOperationMgrFunnelStep1.value
      ?.validate()
      .catch(() => false);
    if (isValid) {
      step.value++;
    }
  } else if (step.value === 2) {
    const foundActionsInProgress = campaignFunnelStep2.value.dtwins.find(
      (dtwin) => dtwin.actionInProgress,
    );
    if (foundActionsInProgress) {
      showConfirmActionsInProgress.value = true;
    } else {
      await nextStepFromStep2();
    }
  }
}

/**
 * Go to the next step from step 2
 */
async function nextStepFromStep2() {
  const isValid = await campaignOperationMgrFunnelStep2.value
    ?.validate()
    .catch(() => false);
  if (isValid) {
    step.value++;
  }
}

/**
 * Confirm go to the next step with actions in progress
 */
function confirmActionsInProgress() {
  showConfirmActionsInProgress.value = false;
  nextStepFromStep2();
}

/**
 * Build operation parameters list
 */
async function buildOperationParametersList() {
  const operationParametersList: CampaignOperationParameter[] = [];

  // get the selected operation models sorted from the latest to the oldest
  const selectedOperationModels = operationModels.value
    .filter(
      (operationModel) =>
        // keep the operation model if it corresponds to the selected operation model type
        operationModel.type === campaignFunnelStep1.value.operationModelType &&
        // keep the operation model if it corresponds to the selected firmware range
        (!selectedFirmware.value?.range ||
          operationModel.deviceTypes.includes(selectedFirmware.value.range)),
    )
    // sort the operation models by version from the latest to the oldest
    .sort((operationModel1, operationModel2) =>
      versionCompare(operationModel2.version, operationModel1.version),
    );

  for (const selectedOperationModel of selectedOperationModels) {
    // for campaign, the specific configuration is not provided by the front and will be added by the AS Web
    const customInputParameters = await getCustomInputParameters({
      organizationCode: currentOrganization.value?.code,
      operationModel: selectedOperationModel,
      firmware: selectedFirmware.value,
      inclusionPeriods: campaignFunnelStep3.value.inclusionPeriods,
    });

    if (LxcError.check(customInputParameters)) {
      return customInputParameters;
    }

    const operationParameters = {
      operationModelUid: selectedOperationModel.uid,
      customInputParameters,
    };

    operationParametersList.push(operationParameters);
  }

  return operationParametersList;
}

/**
 * Get the payload for campaign update
 */
async function getUpdateCampaignPayload() {
  const operationParametersList = await buildOperationParametersList();
  if (LxcError.check(operationParametersList)) {
    return operationParametersList;
  }

  const campaignOperationMgrUpdate: CampaignOperationMgrUpdate = {
    compatibilityCriteria:
      campaignOperationMgrFunnelStep2.value?.compatibilityCriteria,
    operationParametersList,
  };

  const dtwinsDelta = getArrayDelta(
    campaignOperationMgrFunnelStep2.value?.initialSelectedDtwinsUid,
    campaignFunnelStep2.value.dtwins.map((dtwin) => dtwin.uid),
  );
  const fleetsDelta = getArrayDelta(
    campaignOperationMgrFunnelStep2.value?.initialSelectedFleetsUid,
    campaignFunnelStep2.value.fleets.map((fleet) => fleet.uid),
  );

  const deviceOperations: CampaignOperationMgrAddRemoveOperation = {
    add:
      dtwinsDelta.added.length > 0
        ? (dtwinsDelta.added as string[])
        : undefined,
    remove:
      dtwinsDelta.deleted.length > 0
        ? (dtwinsDelta.deleted as string[])
        : undefined,
  };

  const fleetOperations: CampaignOperationMgrAddRemoveOperation = {
    add:
      fleetsDelta.added.length > 0
        ? (fleetsDelta.added as string[])
        : undefined,
    remove:
      fleetsDelta.deleted.length > 0
        ? (fleetsDelta.deleted as string[])
        : undefined,
  };

  if (deviceOperations.add || deviceOperations.remove) {
    campaignOperationMgrUpdate.deviceOperations = deviceOperations;
  }

  if (fleetOperations.add || fleetOperations.remove) {
    campaignOperationMgrUpdate.fleetOperations = fleetOperations;
  }

  const launchPlannedAt =
    formatIsoDateToUtc(campaignFunnelStep3.value.dateRange.startAt) || "";
  if (launchPlannedAt !== props.campaign?.launchPlannedAt) {
    campaignOperationMgrUpdate.launchPlannedAt = launchPlannedAt;
  }

  const expiredAt =
    formatIsoDateToUtc(campaignFunnelStep3.value.dateRange.endAt) || "";
  if (expiredAt !== props.campaign?.expiredAt) {
    campaignOperationMgrUpdate.expiredAt = expiredAt;
  }

  return campaignOperationMgrUpdate;
}

/**
 * Get the payload for campaign creation
 */
async function getCreateCampaignPayload() {
  const operationParametersList = await buildOperationParametersList();

  if (LxcError.check(operationParametersList)) {
    return operationParametersList;
  }

  return {
    name: campaignFunnelStep1.value?.name || "",
    organizationCode: currentOrganization.value?.code || -1,
    deviceTwinUidList: campaignFunnelStep2.value.dtwins.map(
      (dtwin) => dtwin.uid,
    ),
    fleetUidList: campaignFunnelStep2.value.fleets.map((fleet) => fleet.uid),
    compatibilityCriteria:
      campaignOperationMgrFunnelStep2.value?.compatibilityCriteria,
    type: CampaignOMType.STATIC,
    creationMode: CampaignCreationMode.MANUAL,
    launchPlannedAt:
      formatIsoDateToUtc(campaignFunnelStep3.value.dateRange.startAt) || "",
    expiredAt:
      formatIsoDateToUtc(campaignFunnelStep3.value.dateRange.endAt) || "",
    operationParametersList,
  } as CampaignOperationMgrCreate;
}

/**
 * Submit the campaign for creation or update
 */
async function submit() {
  const checkValidity: boolean[] = await campaignOperationMgrFunnelStep3.value
    ?.validate()
    .catch(() => false);
  if (!checkValidity.includes(false)) {
    isSubmitting.value = true;

    // update campaign
    if (props.campaign) {
      const campaign = await getUpdateCampaignPayload();

      if (LxcError.check(campaign)) {
        campaign.notify(NotificationKey.saveError);
      } else {
        const response = await CampaignOperationMgrService.update(
          props.campaign.uid,
          campaign,
          currentOrganization.value?.code,
        );

        if (LxcError.check(response)) {
          response.notify(NotificationKey.saveError);
        } else {
          showNotificationSuccess(t(NotificationKey.saveSuccess));
          emit("saved", props.campaign.uid);
        }
      }
    }
    // create campaign
    else {
      const campaign = await getCreateCampaignPayload();

      if (LxcError.check(campaign)) {
        campaign.notify(NotificationKey.saveError);
      } else {
        const response = await CampaignOperationMgrService.create(
          campaign,
          currentOrganization.value?.code,
        );

        if (LxcError.check(response)) {
          (response as LxcError).notify(NotificationKey.saveError);
        } else {
          showNotificationSuccess(t(NotificationKey.saveSuccess));
          emit("saved", response.uid);
        }
      }
    }

    isSubmitting.value = false;
  }
}

// when showing the funnel:
// - reset the step to first one
// - reset campaign form
watch(
  () => props.show,
  () => {
    step.value = 1;
    resetCampaignFunnel(props.campaign);
  },
);

// when the campaign changed => reset the campaign form
watch(() => props.campaign, resetCampaignFunnel);

// when the operation model type changed => reset the selected devices, fleets and the inclusion periods
watch(
  () => campaignFunnelStep1.value.operationModelType,
  () => {
    resetCampaignFunnelStep2();

    // reset the inclusion periods
    if (
      campaignFunnelStep1.value.operationModelType &&
      !props.campaign?.operationTypes.includes(
        campaignFunnelStep1.value.operationModelType,
      )
    ) {
      resetCampaignFunnelStep3InclusionPeriods();
    }
  },
);
// when the firmware changed => reset the selected devices and fleets
watch(
  () => campaignFunnelStep1.value.firmwareUuid,
  () => resetCampaignFunnelStep2(),
);
</script>

<template>
  <lxc-common-modal
    :show="show"
    is-full-screen
    with-steps
    :step="step"
    :number-of-steps="3"
    @close="$emit('update:show', false)"
  >
    <template #header>
      <h2 v-show="step === 1" class="text-gray-900">
        {{
          campaign
            ? t("campaign.funnel.updateCampaign")
            : t("campaign.funnel.newCampaign")
        }}
      </h2>
      <h2 v-show="step === 2" class="text-gray-900">
        {{ t("campaign.funnel.deviceFleetSelection.title") }}
      </h2>
      <h2 v-show="step === 3" class="text-gray-900">
        {{ t("campaign.funnel.scheduling") }}
      </h2>
      <lxc-button
        v-if="step > 1"
        :title="t('campaign.funnel.previous')"
        type="tertiary"
        class="w-11 h-11 absolute top-1/2 -translate-y-1/2 left-8"
        @click="step--"
      >
        <i-lxc-arrow-left class="text-gray-700" height="20" width="20" />
      </lxc-button>
    </template>

    <template #body>
      <div v-show="step === 1" class="mt-7">
        <campaign-operation-mgr-funnel-step1
          ref="campaignOperationMgrFunnelStep1"
          v-model="campaignFunnelStep1"
          :is-editing="!!props.campaign"
          :can-edit="canEditCampaign"
          @select-firmware="selectedFirmware = $event"
          @all-operation-models-loaded="operationModels = $event"
        />
      </div>
      <div v-show="step === 2" class="mt-3">
        <campaign-operation-mgr-funnel-step2
          ref="campaignOperationMgrFunnelStep2"
          v-model="campaignFunnelStep2"
          :operation-models="operationModels"
          :operation-model-type="campaignFunnelStep1.operationModelType"
          :firmware="selectedFirmware"
          :fleet-uid="campaign?.fleetUid"
          :campaign-status="campaign?.status"
        />
      </div>
      <div v-show="step === 3">
        <campaign-operation-mgr-funnel-step3
          ref="campaignOperationMgrFunnelStep3"
          v-model="campaignFunnelStep3"
          :can-edit="canEditCampaign"
          :operation-models="operationModels"
          :operation-model-type="campaignFunnelStep1.operationModelType"
          :firmware="selectedFirmware"
        />
      </div>
    </template>

    <template #footer>
      <lxc-button
        v-if="step < 3"
        :title="t('campaign.funnel.next')"
        type="primary"
        :disabled="!isCurrentStepValid"
        @click="nextStep"
      >
        {{ t("campaign.funnel.next") }}
      </lxc-button>
      <lxc-button
        v-if="step === 3"
        :title="
          campaign
            ? t('campaign.funnel.updateCampaign')
            : t('campaign.funnel.publish')
        "
        type="primary"
        :is-loading="isSubmitting"
        :disabled="!isCurrentStepValid"
        @click="submit"
      >
        {{
          campaign
            ? t("campaign.funnel.updateCampaign")
            : t("campaign.funnel.publish")
        }}
      </lxc-button>
    </template>
  </lxc-common-modal>

  <confirm-modal
    :is-dialog-visible="showConfirmActionsInProgress"
    :title="t('campaign.funnel.fleetSelection.actions.scheduled.confirm.title')"
    :description="
      t('campaign.funnel.fleetSelection.actions.scheduled.confirm.message')
    "
    :ok-label="
      t('campaign.funnel.fleetSelection.actions.scheduled.confirm.yes')
    "
    :cancel-label="
      t('campaign.funnel.fleetSelection.actions.scheduled.confirm.no')
    "
    :icon="ILxcAlertCircle"
    icon-color-theme="warning"
    @confirm="confirmActionsInProgress"
    @cancel="showConfirmActionsInProgress = false"
  />
</template>
